import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Users } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";

const PALETTE = [
  { type: "text", label: "Short text" },
  { type: "email", label: "Email" },
  { type: "phone", label: "Phone" },
  { type: "textarea", label: "Long text" },
  { type: "number", label: "Number" },
  { type: "date", label: "Date" },
  { type: "select", label: "Dropdown" },
  { type: "radio", label: "Radio choice" },
  { type: "checkbox", label: "Checkbox" },
  { type: "product_list", label: "Product order list" },
  { type: "file", label: "File upload" },
];

const SUBFIELD_TYPES = ["text", "email", "phone", "textarea", "number", "date"];

const DEFAULT_FIELDS = [
  { id: "f_name", type: "text", label: "Your name", placeholder: "Jane Smith", required: true, options: [] },
  { id: "f_email", type: "email", label: "Email address", placeholder: "jane@example.com", required: true, options: [] },
];

const CUSTOMER_DETAILS_PRESET = [
  { id: "sf_name", type: "text", label: "Full name", placeholder: "Jane Smith", required: true },
  { id: "sf_address", type: "textarea", label: "Address", placeholder: "Street, city, postcode", required: true },
  { id: "sf_dob", type: "date", label: "Date of birth", placeholder: "", required: true },
];

export default function FormBuilder() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "Untitled form",
    description: "",
    thank_you_message: "Thank you for your submission. We'll be in touch soon.",
    active: true,
    fields: DEFAULT_FIELDS,
  });
  const [selected, setSelected] = useState(0);
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/business/library").then(({ data }) => setLibrary(data)).catch(() => {});
    api.get("/business/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    if (!isNew) {
      api.get(`/business/forms/${id}`).then(({ data }) => {
        setForm(data);
        setSelected(0);
      }).catch((e) => toast.error(apiError(e)));
    }
  }, [id, isNew]);

  const setField = (idx, patch) => {
    const fields = form.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    setForm({ ...form, fields });
  };

  const addField = (type, label) => {
    const f = {
      id: `f_${Date.now()}`,
      type,
      label,
      placeholder: "",
      required: false,
      options: ["select", "radio"].includes(type) ? ["Option 1", "Option 2"] : [],
    };
    if (type === "group") {
      f.min_sets = 1;
      f.max_sets = 6;
      f.subfields = [
        { id: `sf_${Date.now()}_1`, type: "text", label: "Name", placeholder: "", required: true },
      ];
    }
    if (type === "product_list") {
      f.products = [
        { id: `p_${Date.now()}`, name: "New product", price_label: "", price_value: null, counts_to_total: false, allow_qty: true, allow_note: true, qty_hint: "" },
      ];
    }
    setForm({ ...form, fields: [...form.fields, f] });
    setSelected(form.fields.length);
  };

  const addCustomerDetails = () => {
    const ts = Date.now();
    const f = {
      id: `f_${ts}`,
      type: "group",
      label: "Customer details",
      placeholder: "",
      required: true,
      options: [],
      min_sets: 1,
      max_sets: 6,
      subfields: CUSTOMER_DETAILS_PRESET.map((s, i) => ({ ...s, id: `sf_${ts}_${i}` })),
    };
    setForm({ ...form, fields: [...form.fields, f] });
    setSelected(form.fields.length);
  };

  const applyTemplate = (t) => {
    const isPristine = JSON.stringify(form.fields) === JSON.stringify(DEFAULT_FIELDS);
    if (!isPristine && form.fields.length > 0 && !window.confirm(`Replace the current fields with the "${t.name}" template?`)) return;
    const ts = Date.now();
    setForm({
      ...form,
      title: t.title,
      description: t.description,
      fields: t.fields.map((f, i) => ({ ...f, id: `f_${ts}_${i}` })),
    });
    setSelected(0);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= form.fields.length) return;
    const fields = [...form.fields];
    [fields[idx], fields[j]] = [fields[j], fields[idx]];
    setForm({ ...form, fields });
    setSelected(j);
  };

  const removeField = (idx) => {
    const fields = form.fields.filter((_, i) => i !== idx);
    setForm({ ...form, fields });
    setSelected(Math.max(0, idx - 1));
  };

  const setSubfield = (fIdx, sIdx, patch) => {
    const f = form.fields[fIdx];
    const subfields = f.subfields.map((s, i) => (i === sIdx ? { ...s, ...patch } : s));
    setField(fIdx, { subfields });
  };

  const addSubfield = (fIdx) => {
    const f = form.fields[fIdx];
    const subfields = [...(f.subfields || []), { id: `sf_${Date.now()}`, type: "text", label: "New field", placeholder: "", required: false }];
    setField(fIdx, { subfields });
  };

  const removeSubfield = (fIdx, sIdx) => {
    const f = form.fields[fIdx];
    setField(fIdx, { subfields: f.subfields.filter((_, i) => i !== sIdx) });
  };

  const setProduct = (fIdx, pIdx, patch) => {
    const f = form.fields[fIdx];
    const products = f.products.map((p, i) => (i === pIdx ? { ...p, ...patch } : p));
    setField(fIdx, { products });
  };

  const addProduct = (fIdx) => {
    const f = form.fields[fIdx];
    const products = [...(f.products || []), { id: `p_${Date.now()}`, name: "New product", price_label: "", price_value: null, counts_to_total: false, allow_qty: true, allow_note: true, qty_hint: "" }];
    setField(fIdx, { products });
  };

  const removeProduct = (fIdx, pIdx) => {
    const f = form.fields[fIdx];
    setField(fIdx, { products: f.products.filter((_, i) => i !== pIdx) });
  };

  const toggleOptionDoc = (fIdx, option, docId, checked) => {
    const f = form.fields[fIdx];
    const od = { ...(f.option_docs || {}) };
    const cur = od[option] || [];
    od[option] = checked ? [...cur, docId] : cur.filter((d) => d !== docId);
    setField(fIdx, { option_docs: od });
  };

  const hasEmail = form.fields.some(
    (f) => f.type === "email" || (f.subfields || []).some((s) => s.type === "email")
  );

  const save = async () => {
    if (!hasEmail) {
      toast.error("Add an email field — customers log in with it.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/business/forms", form);
        toast.success("Form created");
        navigate(`/dashboard/forms/${data.id}/edit`);
      } else {
        await api.patch(`/business/forms/${id}`, form);
        toast.success("Form saved");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sel = form.fields[selected];
  const checkboxOptions = sel?.type === "checkbox" ? sel.options || [] : null;

  return (
    <div data-testid="form-builder">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">{isNew ? "New form" : "Edit form"}</h1>
          {!hasEmail && (
            <p className="text-sm text-amber-700 mt-1" data-testid="email-field-warning">
              Add an email field (top-level or inside a repeatable set) so customers can receive replies and log in.
            </p>
          )}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full transition-transform duration-150 hover:-translate-y-0.5" data-testid="save-form-button">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save form"}
        </Button>
      </div>

      <div className="mt-8 grid lg:grid-cols-[220px_1fr_300px] gap-4 items-start">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4" data-testid="field-palette">
          <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-3">Field types</h3>
          <div className="space-y-1.5">
            {PALETTE.map((p) => (
              <button
                key={p.type}
                onClick={() => addField(p.type, p.label)}
                className="w-full flex items-center justify-between text-sm border border-[#E5E5E5] rounded-lg px-3 py-2 hover:bg-neutral-50 transition-colors duration-150"
                data-testid={`palette-${p.type}`}
              >
                {p.label} <Plus className="h-3.5 w-3.5 text-neutral-400" />
              </button>
            ))}
          </div>
          <h3 className="text-xs uppercase tracking-widest text-neutral-400 mt-5 mb-3">Templates</h3>
          <div className="space-y-1.5" data-testid="template-palette">
            {templates.length === 0 && (
              <p className="text-xs text-neutral-400" data-testid="templates-empty-note">No templates available.</p>
            )}
            {templates.map((t) => (
              <button
                key={t.name}
                onClick={() => applyTemplate(t)}
                className="w-full text-left text-xs border border-dashed border-neutral-300 rounded-lg px-3 py-2 hover:border-neutral-900 hover:bg-neutral-50 transition-colors duration-150"
                data-testid={`template-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-neutral-400 mt-0.5 line-clamp-1">{t.description}</div>
              </button>
            ))}
          </div>
          <h3 className="text-xs uppercase tracking-widest text-neutral-400 mt-5 mb-3">Repeatable sets</h3>
          <div className="space-y-1.5">
            <button
              onClick={addCustomerDetails}
              className="w-full flex items-center justify-between text-sm border border-neutral-900 bg-neutral-900 text-white rounded-lg px-3 py-2 hover:bg-neutral-800 transition-colors duration-150"
              data-testid="palette-customer-details"
            >
              <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Customer details</span>
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => addField("group", "Repeater group")}
              className="w-full flex items-center justify-between text-sm border border-[#E5E5E5] rounded-lg px-3 py-2 hover:bg-neutral-50 transition-colors duration-150"
              data-testid="palette-group"
            >
              Custom group <Plus className="h-3.5 w-3.5 text-neutral-400" />
            </button>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="form-canvas">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Form title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description-input" />
            </div>
          </div>
          <div className="mt-6 space-y-2">
            {form.fields.length === 0 && <p className="text-sm text-neutral-400 py-8 text-center">Add fields from the palette.</p>}
            {form.fields.map((f, i) => (
              <div
                key={f.id}
                onClick={() => setSelected(i)}
                className={`flex items-center gap-3 border rounded-xl px-4 py-3 cursor-pointer transition-colors duration-150 ${
                  selected === i ? "border-neutral-900 bg-neutral-50" : "border-[#E5E5E5] hover:border-neutral-400"
                }`}
                data-testid={`canvas-field-${f.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </div>
                  <div className="text-xs text-neutral-400">
                    {f.type === "group"
                      ? `repeatable set · ${(f.subfields || []).length} fields · up to ${f.max_sets || 6} sets`
                      : f.type === "product_list"
                      ? `order list · ${(f.products || []).length} products`
                      : f.type}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="text-neutral-400 hover:text-neutral-900" data-testid={`field-up-${f.id}`}><ArrowUp className="h-4 w-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="text-neutral-400 hover:text-neutral-900" data-testid={`field-down-${f.id}`}><ArrowDown className="h-4 w-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); removeField(i); }} className="text-neutral-400 hover:text-red-600" data-testid={`field-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-[#E5E5E5] space-y-4" data-testid="form-settings-section">
            <h3 className="text-xs uppercase tracking-widest text-neutral-400">Form settings</h3>
            <div className="space-y-2">
              <Label>Thank-you message (emailed to customer)</Label>
              <Textarea
                rows={3}
                value={form.thank_you_message}
                onChange={(e) => setForm({ ...form, thank_you_message: e.target.value })}
                data-testid="thank-you-input"
              />
            </div>
            <div className="border border-[#E5E5E5] rounded-xl p-4 space-y-3" data-testid="autoresponder-settings">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-responder email</Label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Emails the customer a confirmation with the documents assigned to their checked options, plus a confirm-receipt button.
                  </p>
                </div>
                <Switch
                  checked={!!form.autoresponder_enabled}
                  onCheckedChange={(v) => setForm({ ...form, autoresponder_enabled: v })}
                  data-testid="autoresponder-switch"
                />
              </div>
              {form.autoresponder_enabled && (
                <div className="space-y-2">
                  <Label>Auto-responder message</Label>
                  <Textarea
                    rows={3}
                    value={form.autoresponder_message || ""}
                    onChange={(e) => setForm({ ...form, autoresponder_message: e.target.value })}
                    placeholder="Thank you — your documents are attached."
                    data-testid="autoresponder-message-input"
                  />
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deadline (optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.closes_at ? String(form.closes_at).slice(0, 16) : ""}
                  onChange={(e) => setForm({ ...form, closes_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  data-testid="form-deadline-input"
                />
                <p className="text-xs text-neutral-500">Stored and enforced in UTC.</p>
              </div>
              <div className="space-y-2">
                <Label>Message shown when closed</Label>
                <Input
                  value={form.closed_message || ""}
                  onChange={(e) => setForm({ ...form, closed_message: e.target.value })}
                  placeholder="This form is no longer accepting submissions."
                  data-testid="form-closed-message-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4 space-y-4" data-testid="field-properties">
          <h3 className="text-xs uppercase tracking-widest text-neutral-400">Properties</h3>
          {sel ? (
            <>
              <div className="space-y-2">
                <Label>Label</Label>
                <Input value={sel.label} onChange={(e) => setField(selected, { label: e.target.value })} data-testid="prop-label-input" />
              </div>
              {sel.type !== "group" && (
                <div className="space-y-2">
                  <Label>Placeholder</Label>
                  <Input value={sel.placeholder} onChange={(e) => setField(selected, { placeholder: e.target.value })} data-testid="prop-placeholder-input" />
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Required</Label>
                <Switch checked={sel.required} onCheckedChange={(v) => setField(selected, { required: v })} data-testid="prop-required-switch" />
              </div>

              {sel.type === "group" && (
                <div className="space-y-3" data-testid="group-properties">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Min sets</Label>
                      <Input
                        type="number" min={0} max={20}
                        value={sel.min_sets ?? 1}
                        onChange={(e) => setField(selected, { min_sets: Math.max(0, parseInt(e.target.value) || 0) })}
                        data-testid="prop-min-sets-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max sets</Label>
                      <Input
                        type="number" min={1} max={20}
                        value={sel.max_sets ?? 6}
                        onChange={(e) => setField(selected, { max_sets: Math.max(1, parseInt(e.target.value) || 1) })}
                        data-testid="prop-max-sets-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Fields in each set</Label>
                    {(sel.subfields || []).map((sf, si) => (
                      <div key={sf.id} className="border border-[#E5E5E5] rounded-lg p-2 space-y-2" data-testid={`subfield-${sf.id}`}>
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={sf.label}
                            onChange={(e) => setSubfield(selected, si, { label: e.target.value })}
                            className="h-8 text-sm"
                            data-testid={`subfield-label-${sf.id}`}
                          />
                          <button onClick={() => removeSubfield(selected, si)} className="text-neutral-400 hover:text-red-600 shrink-0" data-testid={`subfield-delete-${sf.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <select
                            value={sf.type}
                            onChange={(e) => setSubfield(selected, si, { type: e.target.value })}
                            className="text-xs border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white flex-1"
                            data-testid={`subfield-type-${sf.id}`}
                          >
                            {SUBFIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-neutral-500">
                            <Checkbox
                              checked={sf.required}
                              onCheckedChange={(v) => setSubfield(selected, si, { required: !!v })}
                              data-testid={`subfield-required-${sf.id}`}
                            />
                            Req
                          </label>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full rounded-lg" onClick={() => addSubfield(selected)} data-testid="add-subfield-button">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add field to set
                    </Button>
                  </div>
                </div>
              )}

              {sel.type === "product_list" && (
                <div className="space-y-3" data-testid="product-list-editor">
                  <Label className="text-xs">Products</Label>
                  {(sel.products || []).map((p, pi) => (
                    <div key={p.id} className="border border-[#E5E5E5] rounded-lg p-2 space-y-2" data-testid={`product-${p.id}`}>
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={p.name}
                          onChange={(e) => setProduct(selected, pi, { name: e.target.value })}
                          className="h-8 text-sm"
                          placeholder="Product name"
                          data-testid={`product-name-${p.id}`}
                        />
                        <button onClick={() => removeProduct(selected, pi)} className="text-neutral-400 hover:text-red-600 shrink-0" data-testid={`product-delete-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <Input
                        value={p.price_label}
                        onChange={(e) => setProduct(selected, pi, { price_label: e.target.value })}
                        className="h-8 text-xs"
                        placeholder='Price label, e.g. "£20 per kg" or "Please specify"'
                        data-testid={`product-price-${p.id}`}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-500">Counts toward total</span>
                        <Checkbox
                          checked={!!p.counts_to_total}
                          onCheckedChange={(v) => setProduct(selected, pi, { counts_to_total: !!v })}
                          data-testid={`product-counts-${p.id}`}
                        />
                      </div>
                      {p.counts_to_total && (
                        <Input
                          type="number" step="0.01" min="0"
                          value={p.price_value ?? ""}
                          onChange={(e) => setProduct(selected, pi, { price_value: e.target.value === "" ? null : parseFloat(e.target.value) })}
                          className="h-8 text-xs"
                          placeholder="Unit price £ (multiplied by qty)"
                          data-testid={`product-price-value-${p.id}`}
                        />
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-500">Quantity input</span>
                        <Checkbox
                          checked={p.allow_qty !== false}
                          onCheckedChange={(v) => setProduct(selected, pi, { allow_qty: !!v })}
                          data-testid={`product-qty-${p.id}`}
                        />
                      </div>
                      {p.allow_qty !== false && (
                        <Input
                          value={p.qty_hint || ""}
                          onChange={(e) => setProduct(selected, pi, { qty_hint: e.target.value })}
                          className="h-8 text-xs"
                          placeholder='Qty hint, e.g. "No. of packs / 8s"'
                          data-testid={`product-qty-hint-${p.id}`}
                        />
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-500">Pack / cut size note</span>
                        <Checkbox
                          checked={p.allow_note !== false}
                          onCheckedChange={(v) => setProduct(selected, pi, { allow_note: !!v })}
                          data-testid={`product-note-${p.id}`}
                        />
                      </div>
                      {p.allow_note !== false && (
                        <Input
                          value={p.note_label || ""}
                          onChange={(e) => setProduct(selected, pi, { note_label: e.target.value })}
                          className="h-8 text-xs"
                          placeholder='Note label, e.g. "Pack / cut size" (default)'
                          data-testid={`product-note-label-${p.id}`}
                        />
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full rounded-lg" onClick={() => addProduct(selected)} data-testid="add-product-button">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add product
                  </Button>
                </div>
              )}

              {["select", "radio", "checkbox"].includes(sel.type) && (
                <div className="space-y-2">
                  <Label>{sel.type === "checkbox" ? "Options (one per line, blank = single yes/no)" : "Options (one per line)"}</Label>
                  <Textarea
                    rows={4}
                    value={(sel.options || []).join("\n")}
                    onChange={(e) => setField(selected, { options: e.target.value.split("\n").filter(Boolean) })}
                    data-testid="prop-options-input"
                  />
                </div>
              )}

              {sel.type === "checkbox" && (checkboxOptions.length > 0 || true) && (
                <div className="space-y-2" data-testid="option-docs-editor">
                  <Label className="text-xs">Attach documents to options</Label>
                  {library.length === 0 ? (
                    <p className="text-xs text-neutral-400" data-testid="option-docs-empty">
                      No documents yet — upload PDFs in the Documents page first.
                    </p>
                  ) : (
                    (checkboxOptions.length > 0 ? checkboxOptions : ["true"]).map((opt) => (
                      <div key={opt} className="border border-[#E5E5E5] rounded-lg p-2 space-y-1.5">
                        <div className="text-xs font-medium">{opt === "true" ? "When checked" : opt}</div>
                        {library.map((doc) => (
                          <label key={doc.id} className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
                            <Checkbox
                              checked={((sel.option_docs || {})[opt] || []).includes(doc.id)}
                              onCheckedChange={(v) => toggleOptionDoc(selected, opt, doc.id, !!v)}
                              data-testid={`option-doc-${sel.id}-${opt}-${doc.id}`}
                            />
                            <span className="truncate">{doc.name}</span>
                          </label>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral-400">Select a field to edit it.</p>
          )}
        </div>
      </div>
    </div>
  );
}
